import React, { useState, useCallback, useEffect, useRef } from 'react';
import { User, Bell, Heart, ShoppingCart, Menu, Search, MessageCircle } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getUserNotifications, getFavorites, getTotalUnreadCount, getCacheStats, forceRefresh } from '../../services/api';
import { getImageUrl } from '../../utils/imageUtils';
import MobileMenu from './MobileMenu';
import logo from '../../assets/logo.png'; // Add this import

// Cache keys
const CACHE_KEYS = {
  NOTIFICATIONS: 'header_notifications',
  FAVORITES: 'header_favorites',
  UNREAD_MESSAGES: 'header_unread_messages',
  LAST_FETCH: 'header_last_fetch'
};

// Cache duration in milliseconds (30 minutes)
const CACHE_DURATION = 30 * 60 * 1000;

const Header = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileSearchQuery, setMobileSearchQuery] = useState('');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [unreadMessages, setUnreadMessages] = useState(0);
  
  const navigate = useNavigate();
  const { user } = useAuth();
  const lastFetchRef = useRef(null);

  const unreadNotifications = notifications.filter(n => !n.is_read).length;
  const favoritesCount = favorites.length;

  // Cache helper functions
  const getFromCache = (key) => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      // console.error('Error reading from cache:', error);
      return null;
    }
  };

  const saveToCache = (key, data) => {
    try {
      localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      // console.error('Error saving to cache:', error);
    }
  };

  const shouldFetchData = () => {
    const lastFetch = localStorage.getItem(CACHE_KEYS.LAST_FETCH);
    if (!lastFetch) return true;
    
    const lastFetchTime = parseInt(lastFetch);
    const now = Date.now();
    return (now - lastFetchTime) > CACHE_DURATION;
  };

  const updateLastFetchTime = () => {
    localStorage.setItem(CACHE_KEYS.LAST_FETCH, Date.now().toString());
  };

  const handleSearch = useCallback((e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
    }
  }, [searchQuery, navigate]);

  const handleMobileSearch = useCallback((e) => {
    e.preventDefault();
    if (mobileSearchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(mobileSearchQuery)}`);
      setMobileSearchQuery('');
      setShowMobileMenu(false);
    }
  }, [mobileSearchQuery, navigate]);

  const loadData = useCallback(async () => {
    if (!user?.user_id) {
      setNotifications([]);
      setFavorites([]);
      setUnreadMessages(0);
      return;
    }

    // Check if we should fetch fresh data
    const shouldFetch = shouldFetchData();
    
    if (!shouldFetch) {
      // Load from cache
      const cachedNotifications = getFromCache(CACHE_KEYS.NOTIFICATIONS) || [];
      const cachedFavorites = getFromCache(CACHE_KEYS.FAVORITES) || [];
      const cachedUnreadMessages = getFromCache(CACHE_KEYS.UNREAD_MESSAGES) || 0;
      
      setNotifications(cachedNotifications);
      setFavorites(cachedFavorites);
      setUnreadMessages(cachedUnreadMessages);
      return;
    }

    try {
      // Fetch fresh data
      const [notificationsData, favoritesData, messagesData] = await Promise.all([
        getUserNotifications().catch(() => ({ data: [] })),
        getFavorites(user.user_id).catch(() => ({ data: [] })),
        getTotalUnreadCount(user.user_id).catch(() => ({ combined_total: 0 }))
      ]);

      const newNotifications = notificationsData.data || [];
      const newFavorites = favoritesData.data || [];
      
      // Extract unread messages count
      let newUnreadMessages = 0;
      if (messagesData?.combined_total !== undefined) {
        newUnreadMessages = messagesData.combined_total;
      } else if (messagesData?.total_unread !== undefined) {
        const buyerCount = messagesData.total_unread || 0;
        const sellerCount = messagesData.seller_unread || 0;
        newUnreadMessages = buyerCount + sellerCount;
      }

      // Update state
      setNotifications(newNotifications);
      setFavorites(newFavorites);
      setUnreadMessages(newUnreadMessages);

      // Save to cache
      saveToCache(CACHE_KEYS.NOTIFICATIONS, newNotifications);
      saveToCache(CACHE_KEYS.FAVORITES, newFavorites);
      saveToCache(CACHE_KEYS.UNREAD_MESSAGES, newUnreadMessages);
      updateLastFetchTime();

    } catch (error) {
      // console.error('Error loading header data:', error);
      
      // Fallback to cache if available
      const cachedNotifications = getFromCache(CACHE_KEYS.NOTIFICATIONS) || [];
      const cachedFavorites = getFromCache(CACHE_KEYS.FAVORITES) || [];
      const cachedUnreadMessages = getFromCache(CACHE_KEYS.UNREAD_MESSAGES) || 0;
      
      setNotifications(cachedNotifications);
      setFavorites(cachedFavorites);
      setUnreadMessages(cachedUnreadMessages);
    }
  }, [user]);

  // Effect to load data on mount and when user changes
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Listen for cache refresh events from other components
  useEffect(() => {
    const handleRefreshData = () => {
      // Clear cache and force fresh fetch
      localStorage.removeItem(CACHE_KEYS.LAST_FETCH);
      loadData();
    };

    // Listen for custom events to refresh data
    window.addEventListener('refreshHeaderData', handleRefreshData);
    
    return () => {
      window.removeEventListener('refreshHeaderData', handleRefreshData);
    };
  }, [loadData]);

  const handleSellClick = () => {
    if (user) {
      navigate('/store');
    } else {
      navigate('/store', { state: { from: 'store/listings/create-listing' } });
    }
  };

  // Optional: Force refresh button for testing
  const handleForceRefresh = async () => {
    try {
      // Clear cache
      localStorage.removeItem(CACHE_KEYS.LAST_FETCH);
      localStorage.removeItem(CACHE_KEYS.NOTIFICATIONS);
      localStorage.removeItem(CACHE_KEYS.FAVORITES);
      localStorage.removeItem(CACHE_KEYS.UNREAD_MESSAGES);
      
      // Force fresh fetch
      await loadData();
      // console.log('Header data refreshed manually');
    } catch (error) {
      // console.error('Error forcing refresh:', error);
    }
  };

  // Optional: Add refresh on focus (if you want to refresh when user returns to tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && shouldFetchData()) {
        // Tab is visible and cache is expired
        loadData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loadData]);

  return (
    <>
      <header className="bg-white shadow-sm sticky top-0 z-40 border-b">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            {/* Left: Logo & Mobile Menu */}
            <div className="flex items-center gap-3">
              <button
                className="md:hidden p-2 hover:bg-[#f5f5f5] rounded-lg"
                onClick={() => setShowMobileMenu(true)}
              >
                <Menu className="w-6 h-6" />
              </button>

              <Link to="/" className="flex items-center gap-3">
                {/* Replace the orange shopping cart icon with logo.png */}
                <div className="flex items-center justify-center">
                  <img 
                    src={logo} 
                    alt="EcoMart Logo" 
                    className="h-10 w-auto object-contain"
                    onError={(e) => {
                      e.target.onerror = null;
                      // Fallback to original icon if logo fails to load
                      e.target.style.display = 'none';
                      const fallbackDiv = e.target.parentNode.querySelector('.logo-fallback');
                      if (fallbackDiv) fallbackDiv.style.display = 'flex';
                    }}
                  />
                  {/* Fallback icon (hidden by default) */}
                  <div className="logo-fallback hidden bg-[#ff660e] p-2 rounded-lg">
                    <ShoppingCart className="w-7 h-7 text-white" />
                  </div>
                </div>
                {/* <div>
                  <h1 className="text-xl font-bold text-gray-900">EcoMart</h1>
                  <p className="text-xs text-[#ff660e] font-medium hidden sm:block">Buy Smart. Sell Safe.</p>
                </div> */}
              </Link>
              
              {/* Optional: Cache indicator for debugging */}
              {/* {process.env.NODE_ENV === 'development' && (
                <button 
                  onClick={handleForceRefresh}
                  className="text-xs text-gray-500 hover:text-[#ff660e]"
                  title="Force refresh header data"
                >
                  Refresh
                </button>
              )} */}
            </div>

            {/* Center: Search Bar (Desktop Only) */}
            <div className="hidden md:flex flex-1 max-w-2xl">
              <form onSubmit={handleSearch} className="relative w-full">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search products, stores, categories..."
                  className="w-full pl-5 pr-14 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#ff660e]"
                />
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-[#ff660e] text-white p-3 rounded-lg hover:opacity-90"
                >
                  <Search className="w-5 h-5" />
                </button>
              </form>
            </div>

            {/* Right: Icons & Actions */}
            <div className="flex items-center gap-3 sm:gap-4">              
              <Link to="/notifications" className="relative p-2 hover:bg-[#f5f5f5] rounded-lg">
                <Bell className="w-5 h-5" />
                {unreadNotifications > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#ff660e] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                    {unreadNotifications}
                  </span>
                )}
              </Link>

              <Link to="/favorites" className="relative p-2 hover:bg-[#f5f5f5] rounded-lg">
                <Heart className="w-5 h-5" />
                {favoritesCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#ff660e] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                    {favoritesCount}
                  </span>
                )}
              </Link>

              <Link to="/chats" className="relative p-2 hover:bg-[#f5f5f5] rounded-lg">
                <MessageCircle className="w-5 h-5" />
                {unreadMessages > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#ff660e] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                    {unreadMessages > 99 ? '99+' : unreadMessages}
                  </span>
                )}
              </Link>

              {user ? (
                <Link 
                  to="/profile" 
                  className="hidden md:flex items-center gap-2 p-2 hover:bg-[#f5f5f5] rounded-lg transition-colors"
                >
                  {user.profile_picture_url ? (
                    <img 
                      src={getImageUrl(user.profile_picture_url)} 
                      alt={`${user.first_name || user.username || 'User'}'s profile`}
                      className="w-8 h-8 rounded-full object-cover border border-gray-200"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.style.display = 'none';
                        // Create fallback avatar
                        const fallbackDiv = document.createElement('div');
                        fallbackDiv.className = 'w-8 h-8 bg-[#ff660e] rounded-full flex items-center justify-center text-white text-sm font-bold';
                        fallbackDiv.textContent = user.first_name?.[0] || user.username?.[0] || 'U';
                        e.target.parentNode.appendChild(fallbackDiv);
                      }}
                    />
                  ) : (
                    <div className="w-8 h-8 bg-[#ff660e] rounded-full flex items-center justify-center text-white text-sm font-bold">
                      {user.first_name?.[0] || user.username?.[0] || 'U'}
                    </div>
                  )}
                  <span className="hidden lg:inline text-sm font-medium">
                    Hi, {user.first_name || 'Account'}
                  </span>
                </Link>
              ) : (
                <div className="hidden md:flex items-center gap-3">
                  <Link to="/login" className="text-sm font-medium hover:text-[#ff660e]">
                    Sign In
                  </Link>
                  <Link to="/register" className="px-4 py-2 bg-[#ff660e] text-white rounded-lg font-medium hover:opacity-90">
                    Join Free
                  </Link>
                </div>
              )}

              <button
                onClick={handleSellClick}
                className="px-4 py-2.5 bg-[#ff660e] text-white rounded-lg font-bold hover:opacity-90 hidden sm:block"
              >
                SELL
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* MobileMenu */}
      <MobileMenu
        showMobileMenu={showMobileMenu}
        setShowMobileMenu={setShowMobileMenu}
        user={user}
        favoritesCount={favoritesCount}
        unreadNotifications={unreadNotifications}
        unreadMessages={unreadMessages}
        onSellClick={handleSellClick}
      />
    </>
  );
};

// Export helper function to force refresh from other components
export const refreshHeaderData = () => {
  window.dispatchEvent(new CustomEvent('refreshHeaderData'));
};

export default Header;